import moduleConfigs from '../config/moduleConfigs.json';

export interface ModuleItem {
  id: string;
  label: string;
  path: string;
}

/**
 * Flattens all menu items from moduleConfigs.json into a single array
 * Returns modules with id, label, and path, sorted alphabetically by label
 */
export function getAllModules(): ModuleItem[] {
  const modules: ModuleItem[] = [];
  
  moduleConfigs.menuSections.forEach((section) => {
    if (section.items) {
      section.items.forEach((item) => {
        // Only include items that have a path
        if (item.path) {
          modules.push({
            id: item.id,
            label: item.label,
            path: item.path
          });
        }
      });
    }
  });
  
  // Sort alphabetically by label
  return modules.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Checks if a URL is internal (matches a module path) or external
 * Returns an object with type and resolved path
 */
export function getUrlType(url: string): { type: 'internal' | 'external'; path: string } {
  if (!url || !url.trim()) {
    return { type: 'external', path: '' };
  }
  
  const trimmedUrl = url.trim();
  
  // Check if it's an external URL (starts with http:// or https://)
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return { type: 'external', path: trimmedUrl };
  }
  
  // Extract path from URL if it's a full URL without protocol
  // For internal routes, we expect paths like "/library" or "/books"
  let path = trimmedUrl;
  
  // If it contains a pathname (e.g., "example.com/library"), extract just the path
  try {
    // If it doesn't start with /, assume it might be a relative path or try to parse
    if (!path.startsWith('/')) {
      // Try to see if it's a path-like string without protocol
      if (path.includes('/')) {
        const parts = path.split('/');
        // If first part looks like a domain, extract path
        if (parts[0].includes('.')) {
          path = '/' + parts.slice(1).join('/');
        } else {
          // Otherwise assume it's a relative path, add leading slash
          path = '/' + path;
        }
      } else {
        // Single word, treat as relative path
        path = '/' + path;
      }
    }
  } catch (e) {
    // If parsing fails, use as-is
  }
  
  // Check if the path matches any module path (including detail URLs like /novel/123)
  const modules = getAllModules();
  const matchingModule = modules.find(module => module.path === path);
  
  if (matchingModule) {
    return { type: 'internal', path: trimmedUrl };
  }
  
  // Check if it's a detail URL (e.g., /novel/123 or /people/456)
  const detailParse = parseDetailUrl(path);
  if (detailParse && detailParse.basePath) {
    // It's a detail URL for a module that supports detail pages
    return { type: 'internal', path: trimmedUrl };
  }
  
  // If no match found, treat as external (could be a relative path within the app that's not in config)
  // Or it could be a malformed URL - we'll treat it as external
  return { type: 'external', path: trimmedUrl };
}

/**
 * Checks if a URL matches any module path from moduleConfigs.json
 */
export function isInternalUrl(url: string): boolean {
  return getUrlType(url).type === 'internal';
}

/**
 * Modules that support detail pages with item IDs
 */
const MODULES_WITH_DETAIL_PAGES: Record<string, { service: 'people' | 'novel' | 'notes' | 'knowledge' | 'whiteboard' | 'book' | 'memory'; idField: string }> = {
  '/people': { service: 'people', idField: 'id' },
  '/novel': { service: 'novel', idField: 'novel_id' },
  '/notes': { service: 'notes', idField: 'note_id' },
  '/knowledge': { service: 'knowledge', idField: 'category_id' },
  '/whiteboard': { service: 'whiteboard', idField: 'id' },
  '/books': { service: 'book', idField: 'id' },
  '/memory': { service: 'memory', idField: 'id' }
};

/**
 * Checks if a module path supports detail pages with item selection
 */
export function moduleSupportsDetailPages(modulePath: string): boolean {
  return modulePath in MODULES_WITH_DETAIL_PAGES;
}

/**
 * Gets the service type for a module that supports detail pages
 */
export function getModuleServiceType(modulePath: string): 'people' | 'novel' | 'notes' | 'knowledge' | 'whiteboard' | 'book' | 'memory' | null {
  return MODULES_WITH_DETAIL_PAGES[modulePath]?.service || null;
}

/**
 * Gets the ID field name for a module that supports detail pages
 */
export function getModuleIdField(modulePath: string): string | null {
  return MODULES_WITH_DETAIL_PAGES[modulePath]?.idField || null;
}

/**
 * Extracts the base module path and item ID from a full URL
 * e.g., "/novel/123" -> { basePath: "/novel", itemId: "123" }
 * e.g., "/people/456" -> { basePath: "/people", itemId: "456" }
 */
export function parseDetailUrl(url: string): { basePath: string; itemId: string | null } | null {
  if (!url || !url.startsWith('/')) {
    return null;
  }
  
  const parts = url.split('/').filter(p => p);
  if (parts.length === 0) {
    return null;
  }
  
  const basePath = '/' + parts[0];
  
  // Check if this module supports detail pages
  if (!moduleSupportsDetailPages(basePath)) {
    return null;
  }
  
  // If there's a second part, it's the item ID
  if (parts.length >= 2) {
    return { basePath, itemId: parts[1] };
  }
  
  return { basePath, itemId: null };
}

